import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AuthShell from './AuthShell';
import Toast from '../../components/forms/Toast';
import Icon from '../../components/common/Icon';
import { apiRequest, handleBranding, formatDate } from '../../utils';

/* Public accept page (/network-invite/:token). An ecosystem member shared a personal invite
   link; accepting makes the person part of the member's NETWORK (a person↔person connection)
   and — when the invite named a Lab — sends them to that Lab's page to register.
   Backend: GET /api/bravos/network/invite/:token + POST .../accept. */
const NetworkInviteAccept = () => {
  const { t } = useTranslation();
  const { token } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  const [loginFirst, setLoginFirst] = useState(false);

  useEffect(() => {
    handleBranding();
    (async () => {
      try {
        setInfo(await apiRequest('GET', `/bravos/network/invite/${token}`));
      } catch (err) {
        setError(t(err.message, { defaultValue: err.message }));
        setShowToast(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [token, t]);

  const accept = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const data = await apiRequest('POST', `/bravos/network/invite/${token}/accept`, {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        mobile: mobile.trim(),
        email: email.trim(),
      });
      localStorage.setItem('isLoggedIn', '1');
      window.dispatchEvent(new Event('auth-changed'));
      // A voucher-carrying invite hands the code to the checkout via sessionStorage (survives
      // the redirect; MemberCheckout reads + pre-applies it for the named Lab).
      if (data.voucher_code && info && info.event_name !== undefined) {
        const m = /\/book\/([^/?]+)/.exec(data.redirect || '');
        if (m) {
          try {
            window.sessionStorage.setItem('bravosBookVoucher',
              JSON.stringify({ event_id: m[1], code: data.voucher_code }));
          } catch (_) { /* storage unavailable — the member can still type the code */ }
        }
      }
      navigate(data.redirect || '/member-home');
    } catch (err) {
      if (err.message === 'accountExistsLoginFirst') {
        setLoginFirst(true);
      } else {
        setError(t(err.message, { defaultValue: err.message }));
        setShowToast(true);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const valid = info && info.valid;
  const subtitle = valid
    ? [info.event_date ? formatDate(info.event_date) : null, info.location_name]
      .filter(Boolean).join(' · ')
    : '';

  return (
    <AuthShell
      title={valid ? (info.event_name || t('networkInviteTitle')) : t('networkInviteTitle')}
      subtitle={subtitle}
    >
      {loading ? (
        <div className="auth__card"><span className="auth__spinner" /></div>
      ) : !valid ? (
        <div className="auth__card">
          <p>{t('invalidLink')}</p>
        </div>
      ) : loginFirst ? (
        <div className="auth__card">
          <p className="auth__hint">
            <Icon name="user-check" /> {t('inviteLoginFirst')}
          </p>
          <button
            type="button"
            className={`auth__primary${localStorage.getItem('primaryColor') ? '' : ' auth__primary--plg'}`}
            onClick={() => navigate('/login')}
          >
            <Icon name="sign-in-alt" /> {t('login')}
          </button>
        </div>
      ) : (
        <form className="auth__card" onSubmit={accept}>
          <p className="auth__hint" style={{ fontWeight: 600 }}>
            <Icon name="user-check" /> {t('invitedByMember', { name: info.inviter_name })}
          </p>
          {info.voucher && (
            <p className="auth__hint" style={{ fontWeight: 600, color: '#1a7f4b' }}>
              <Icon name="ticket" /> {t('voucherOfferHeading', { offer: info.voucher.offer })}
            </p>
          )}

          <div className="auth__row">
            <div className="auth__field">
              <label className="auth__label">{t('firstName')}</label>
              <div className="auth__input-wrap">
                <input
                  className="auth__input"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder={t('enterFirstName')}
                  autoComplete="given-name"
                  required
                />
              </div>
            </div>
            <div className="auth__field">
              <label className="auth__label">{t('lastName')}</label>
              <div className="auth__input-wrap">
                <input
                  className="auth__input"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder={t('enterLastName')}
                  autoComplete="family-name"
                  required
                />
              </div>
            </div>
          </div>

          <div className="auth__field">
            <label className="auth__label">{t('email')}</label>
            <div className="auth__input-wrap">
              <input
                className="auth__input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('enterEmail')}
                autoComplete="email"
                required
              />
            </div>
          </div>

          <div className="auth__field">
            <label className="auth__label">{t('mobileNumber')}</label>
            <div className="auth__input-wrap">
              <input
                className="auth__input"
                type="tel"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                placeholder={t('enterMobile')}
                autoComplete="tel"
              />
            </div>
          </div>

          <button
            type="submit"
            className={`auth__primary${localStorage.getItem('primaryColor') ? '' : ' auth__primary--plg'}`}
            disabled={submitting}
          >
            {submitting ? <span className="auth__spinner" /> : <Icon name="user-add" />}
            {t('acceptInvite')}
          </button>

          <Toast showToast={showToast} setShowToast={setShowToast} message={error} />
        </form>
      )}
    </AuthShell>
  );
};

export default NetworkInviteAccept;
